// climateRaster.ts — fetches and decodes a planet's baked climate raster from
// GET /api/planet/{id}/climate (server climate.EncodeClimate). The body is a zlib-deflated
// header + six uint8 equirectangular planes; we inflate with DecompressionStream and expose
// the planes as typed arrays. Fetches are cached + in-flight-deduped per planet id, so the
// focused planet pays the network once and re-focusing is instant.
import { scopedUrl } from '../../state/scope';

export interface ClimateRaster {
  nx: number;
  ny: number;
  /** Normalized elevation (0..1) below which a cell is ocean. */
  seaLevel: number;
  /** Dequant range for the temperature plane (°C). */
  tempMin: number;
  tempMax: number;
  // Equirect uint8 planes, row-major (row 0 = +90° lat, col 0 = -180° lon), length nx*ny.
  elevation: Uint8Array;
  temp: Uint8Array;
  ice: Uint8Array;
  vegetation: Uint8Array;
  precip: Uint8Array;
  biome: Uint8Array;
}

// Kinds with no surface climate — the server's /api/planet/{id}/climate returns 404 for
// these (mirrors galaxy.climateWorthy), so the client skips the fetch rather than log a 404.
const SURFACELESS = new Set<string>(['gas', 'molten', 'broken', 'barren_dry', 'barren_cold']);

/** Whether a planet kind has a baked climate raster to fetch. */
export function hasSurfaceClimate(kind: string): boolean {
  return !SURFACELESS.has(kind);
}

const cache = new Map<string, Promise<ClimateRaster | null>>();

/** Fetch a planet's climate raster (null for gas giants / lava / airless / unknown ids). */
export function fetchPlanetClimate(id: string): Promise<ClimateRaster | null> {
  let p = cache.get(id);
  if (!p) {
    p = load(id).catch((e) => {
      cache.delete(id); // let a transient failure be retried
      throw e;
    });
    cache.set(id, p);
  }
  return p;
}

async function load(id: string): Promise<ClimateRaster | null> {
  const res = await fetch(scopedUrl(`/api/planet/${encodeURIComponent(id)}/climate`), {
    headers: { Accept: 'application/octet-stream' },
  });
  if (res.status === 404) return null; // surfaceless body or unknown id
  if (!res.ok) throw new Error(`climate ${id}: HTTP ${res.status}`);
  return parse(await inflate(await res.arrayBuffer()));
}

async function inflate(buf: ArrayBuffer): Promise<ArrayBuffer> {
  // Go's compress/zlib emits the zlib format (RFC 1950) -> DecompressionStream('deflate').
  const stream = new Response(buf).body!.pipeThrough(new DecompressionStream('deflate'));
  return new Response(stream).arrayBuffer();
}

function parse(buf: ArrayBuffer): ClimateRaster {
  const dv = new DataView(buf);
  const magic = String.fromCharCode(dv.getUint8(0), dv.getUint8(1), dv.getUint8(2), dv.getUint8(3));
  if (magic !== 'FCL1') {
    throw new Error(`climate: bad magic "${magic}"`);
  }
  const nx = dv.getUint16(4);
  const ny = dv.getUint16(6);
  const seaLevel = dv.getFloat32(8);
  const tempMin = dv.getFloat32(12);
  const tempMax = dv.getFloat32(16);
  const n = nx * ny;
  const bytes = new Uint8Array(buf);
  let off = 20;
  const plane = () => {
    const a = bytes.subarray(off, off + n);
    off += n;
    return a;
  };
  return {
    nx,
    ny,
    seaLevel,
    tempMin,
    tempMax,
    elevation: plane(),
    temp: plane(),
    ice: plane(),
    vegetation: plane(),
    precip: plane(),
    biome: plane(),
  };
}

// sampleEquirect bilinearly samples a uint8 plane by sphere direction, matching the server
// Grid layout exactly: column 0..nx spans lon -180..180, row 0..ny spans lat +90..-90, cell
// centres offset half a step. Longitude wraps, latitude clamps. Returns 0..1. (Both server
// and client use lon = atan2(z, x) with y as the pole, so no rotation offset is needed.)
export function sampleEquirect(
  plane: Uint8Array,
  nx: number,
  ny: number,
  dx: number,
  dy: number,
  dz: number,
): number {
  const lat = Math.asin(Math.max(-1, Math.min(1, dy)));
  const lon = Math.atan2(dz, dx);
  const fx = ((lon + Math.PI) / (2 * Math.PI)) * nx - 0.5;
  let fy = ((Math.PI / 2 - lat) / Math.PI) * ny - 0.5;
  if (fy < 0) fy = 0;
  if (fy > ny - 1) fy = ny - 1;
  const x0 = Math.floor(fx);
  const y0 = Math.floor(fy);
  const tx = fx - x0;
  const ty = fy - y0;
  const wrap = (x: number) => ((x % nx) + nx) % nx;
  const ix0 = wrap(x0);
  const ix1 = wrap(x0 + 1);
  const iy0 = Math.max(0, Math.min(ny - 1, y0));
  const iy1 = Math.max(0, Math.min(ny - 1, y0 + 1));
  const v00 = plane[iy0 * nx + ix0];
  const v10 = plane[iy0 * nx + ix1];
  const v01 = plane[iy1 * nx + ix0];
  const v11 = plane[iy1 * nx + ix1];
  const top = v00 + (v10 - v00) * tx;
  const bot = v01 + (v11 - v01) * tx;
  return (top + (bot - top) * ty) / 255;
}
